import React, { useState, ReactElement } from 'react';
import compact from 'lodash/compact';
import get from 'lodash/get';
import find from 'lodash/find';
import GoogleMapReact, { MapOptions } from 'google-map-react';

import { GeoPoint } from '../../lib/helpers';
import { RichTextField } from '../RichTextField/RichTextField';
import SchoolLocationCard from '../SchoolLocationCard';
import { MapFragment } from '../../../graphql.schema';
import defaultStyle from './styles/colour.json';

import useInViewPort from './lazy-load';
import * as styles from './Map.module.scss';

export type MarkerPropType = GeoPoint & {
  id: string | number;
  popUpHtml?: string | null;
  schoolLocationCard?: ReactElement;
};

type MarkerProps = GeoPoint & {
  marker: MarkerPropType;
  onClick: () => void;
  isOpen: boolean;
  onHover: () => void;
};

export type MapProps = MapFragment & {
  defaultCenter?: GeoPoint | null;
  mapOptions?: MapOptions;
  selectedTags?: string[];
  schoolSlugBase?: string;
  shouldOpenLocationCard?: boolean;
};

const Marker = ({ marker, onClick, onHover, isOpen }: MarkerProps) => {
  return (
    <div data-testid={`marker-${marker.documentId}`} id={`wse-map-marker-${marker.documentId}`} className={styles.mapMarker} onMouseOver={onHover} onClick={onClick}>
      <div className={styles.mapRedPin} />
      {isOpen && marker.popUpHtml && <RichTextField className={styles.mapPopUp} content={marker.popUpHtml} />}
    </div>
  );
};

const Map = ({
  markers,
  width,
  height,
  defaultCenter = undefined,
  defaultZoom = 11,
  mapOptions = undefined,
  schoolSlugBase = "",
  selectedTags = [],
  shouldOpenLocationCard,
}: MapProps) => {

  const [isMapVisible, setIsMapVisible] = useState(false);
  const [nodeRef, isVisible] = useInViewPort(isMapVisible);

  let calculatedDefaultCenter = defaultCenter ?? { lat: 0, lng: 0 };
  if (!defaultCenter?.lat && markers && markers.length > 0 && markers?.[0]) {
    calculatedDefaultCenter = { lat: markers?.[0]?.lat, lng: markers?.[0]?.lng };
  }

  const [openId, setOpenId] = useState<string | number | null>(0);
  const [openSchoolCard, setOpenSchoolCard] = useState<string | number | null>(0);

  const toggleOpenMarker = (toToggle: MarkerPropType) => {
    for (const marker of compact(markers)) {
      if (marker === toToggle) {
        if (openId === marker.documentId) {
          setOpenId(null);
        } else {
          setOpenId(marker.documentId);
        }
        break;
      }
    }
  };

  const openSchoolCardToggle = (toToggle: MarkerPropType) => {
    for (const marker of compact(markers)) {
      if (marker === toToggle) {
        if (openSchoolCard === marker.documentId) {
          setOpenSchoolCard(null);
        } else {
          setOpenSchoolCard(marker.documentId);
        }
        break;
      }
    }
  };


  const getSchoolLocationCard = () => {
    const school = get(find(markers, ['school.documentId', openSchoolCard]), 'school', {});
    if (school) {
      return <SchoolLocationCard
        isMapCard={true}
        onClose={() => setOpenSchoolCard(null)}
        schoolSlugBase={schoolSlugBase}
        selectedTags={selectedTags}
        key={`filtered-school-location-card-${school.documentId}`}
        mapLocation={school?.mapMarker?.mapLocation}
        {...school}
      />
    }
  };

  const optionsFn = () => (mapOptions ? { ...mapOptions, styles: mapOptions.styles ?? defaultStyle } : {});

  return (
    <div ref={nodeRef} style={{ width, height }}>
      {isVisible ? <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.GATSBY_GOOGLE_MAPS_KEY ?? '' }}
        defaultCenter={calculatedDefaultCenter}
        defaultZoom={defaultZoom}
        options={optionsFn}
        onGoogleApiLoaded={() => { setIsMapVisible(true) }}
      >
        {compact(markers).map((marker) => (
          <Marker
            key={marker.documentId}
            marker={marker}
            lat={marker.lat}
            lng={marker.lng}
            onHover={() => toggleOpenMarker(marker)}
            onClick={() => openSchoolCardToggle(marker)}
            isOpen={openId === marker.documentId || openSchoolCard === marker.documentId}
          />
        ))}
      </GoogleMapReact> : null}
      {isVisible && shouldOpenLocationCard && openSchoolCard ? getSchoolLocationCard() : null}
    </div>
  );
};

export default Map;

